import { Box, Button, Chip, Grid2 as Grid, MenuItem, Select, Stack, Tooltip } from "@mui/material";
import React, { FC, useState } from "react";
import { GridColDef, GridRowParams, GridActionsCellItem, GridDeleteIcon } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { DbJobRuleInfos } from "../../../../Core/Api/Types/db-job-rule-infos";
import { JobStatus } from "../../../../Core/Api/Types/job-status";
import FormSection from "../../../../Components/Forms/FormSection/form-section";
import FormAutocomplete from "../../../../Components/Forms/FormAutocomplete/form-autocomplete";
import { produce } from "immer";
import { ObjectStatus } from "../../../../Core/Api/Types/object-status";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { DbJobRulePostActionInfos } from "../../../../Core/Api/Types/db-job-rule-post-action-infos";
import { PostActionType } from "../../../../Core/Api/Types/post-action-type";
import EventIcon from "@mui/icons-material/Event";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import { v4 as uuidv4 } from "uuid";
import { DeleteSection } from "../../../../Redux/Reducers/redux-slice-form-validation";
import { useDispatch } from "react-redux";
import FormSectionValidity from "../../../../Components/Forms/FormSectionNotValidWarningTrigger/form-section-validity";
import TableCellAlertSeverity from "../../../../Components/Table/TableCellAlertSeverity/table-cell-alert-severity";
import TableCellChip from "../../../../Components/Table/TableCellChip/table-cell-chip";
import { cacheService } from "../../../../Core/Api/services";
import { CreateNewDbMetadataValue } from "../../../../Core/Utils/asset-object-utils";
import MetadataInputIndex from "../../../../Components/MetadataInput/metadata-input-index";
import { BaseMetadataType } from "../../../../Core/Api/Types/base-metadata-type";
import { useTranslation } from "react-i18next";

export type EditPostActionsProps = {
  formId: string;
  newJobRule: DbJobRuleInfos;
  setNewJobRule: React.Dispatch<React.SetStateAction<DbJobRuleInfos | null>>;
};

const EditPostActions: FC<EditPostActionsProps> = (props) => {
  const { t } = useTranslation();
  const { newJobRule, setNewJobRule, formId } = props;

  const dispatch = useDispatch();
  const metas = cacheService.GetMetadata();

  const [selectedNewPostActionType, setSelectedNewPostActionType] = useState<PostActionType | undefined>(undefined);
  const [selectedPostActionRow, setSelectedPostActionsRow] = useState<number | null>(null);

  const postActionsCols: GridColDef[] = [
    {
      field: "type",
      headerName: t("common:type"),
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (params) => (
        <FormSectionValidity
          formId={formId}
          sectionId={params?.row?.uuid}
          showWhenValid={<Chip label={params.value} sx={{ ml: 0.5 }} />}
          showWhenNotValid={<TableCellAlertSeverity color="error" data={params} />}
        />
      ),
    },
    {
      field: "jobStatuses",
      headerName: t("table:headerName.jobStatus"),
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (params) => <TableCellChip data={params} />,
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      align: "right",
      flex: CellSize.actionsSmall,
      minWidth: CellSize.actionsSmall,
      getActions: (params: GridRowParams) => {
        if (!params || !params.row) return [];
        let actions: any[] = [];
        if ((params.id as number) > 0) {
          actions.push(
            <GridActionsCellItem
              icon={<ArrowUpwardIcon color="action" />}
              label={t("common:moveUp")}
              onClick={() => {
                if (selectedPostActionRow !== null && selectedPostActionRow === (params.id as number))
                  setSelectedPostActionsRow((x) => x! - 1);
                if (selectedPostActionRow !== null && selectedPostActionRow + 1 === (params.id as number))
                  setSelectedPostActionsRow((x) => x! + 1);
                setNewJobRule(
                  produce((draft) => {
                    if (draft?.postActions) {
                      const tempJob = { ...draft.postActions[(params.id as number) - 1] };
                      draft.postActions[(params.id as number) - 1] = {
                        ...draft.postActions[params.id as number],
                      };
                      draft.postActions[params.id as number] = tempJob;
                    }
                  })
                );
              }}
            />
          );
        }
        if (newJobRule && newJobRule.postActions && (params.id as number) < newJobRule.postActions.length - 1) {
          actions.push(
            <GridActionsCellItem
              icon={<ArrowDownwardIcon color="action" />}
              label={t("common:moveDown")}
              onClick={() => {
                if (selectedPostActionRow !== null && selectedPostActionRow === (params.id as number))
                  setSelectedPostActionsRow((x) => x! + 1);
                if (selectedPostActionRow !== null && selectedPostActionRow - 1 === (params.id as number))
                  setSelectedPostActionsRow((x) => x! - 1);
                setNewJobRule(
                  produce((draft) => {
                    const tempPostAction = { ...draft!.postActions![(params.id as number) + 1] };
                    draft!.postActions![(params.id as number) + 1] = {
                      ...draft!.postActions![params.id as number],
                    };
                    draft!.postActions![params.id as number] = tempPostAction;
                  })
                );
              }}
            />
          );
        }
        actions.push(
          <GridActionsCellItem
            icon={
              <Tooltip title={t("tooltip:title.removePostAction")}>
                <GridDeleteIcon color="error" />
              </Tooltip>
            }
            label={t("common:remove")}
            onClick={() => {
              dispatch(DeleteSection({ formId: formId, sectionId: params?.row?.uuid }));
              setNewJobRule(
                produce((draft) => {
                  draft!.postActions!.splice(params.id as number, 1);
                })
              );
              if (selectedPostActionRow && (params.id as number) < selectedPostActionRow)
                setSelectedPostActionsRow((x) => x! - 1);
            }}
          />
        );
        actions.push(
          <GridActionsCellItem
            icon={
              <Tooltip title={t("common:configurePostAction")}>
                <MiscellaneousServicesIcon />
              </Tooltip>
            }
            label={t("common:configurePostActionLowerCase")}
            disabled={selectedPostActionRow === (params.id as number)}
            onClick={() => setSelectedPostActionsRow(params.id as number)}
          />
        );
        return actions;
      },
    },
  ];

  const handleAddPostAction = () => {
    setNewJobRule(
      produce((draft) => {
        if (!selectedNewPostActionType) return;
        const newPostAction = new DbJobRulePostActionInfos({
          $type:
            selectedNewPostActionType === PostActionType.SetStatus
              ? "VDM.AirLab.Sdk.Core.Database.Types.DbJobRulePostActionChangeObjectStatusInfos, VDM.AirLab.Sdk.Core"
              : selectedNewPostActionType === PostActionType.SetMetadata
              ? "VDM.AirLab.Sdk.Core.Database.Types.DbJobRulePostActionSetMetadataInfos, VDM.AirLab.Sdk.Core"
              : selectedNewPostActionType === PostActionType.SendEmail
              ? "DbJobRulePostActionSendEmailInfos, VDM.AirLab.Sdk.Core"
              : "VDM.AirLab.Sdk.Core.Database.Types.DbJobRulePostActionInfos, VDM.AirLab.Sdk.Core",
          type: selectedNewPostActionType,
          enabled: true,
          jobStatuses: [],
          uuid: uuidv4(),
          status: null,
        });
        draft!.postActions = draft!.postActions ? [...draft!.postActions, newPostAction] : [newPostAction];
      })
    );

    setSelectedPostActionsRow(newJobRule.postActions ? newJobRule.postActions.length : 0);
  };

  return (
    <FormSection size="small" icon={<EventIcon />} defaultExpanded={true} title={t("form:section.title.postActions")}>
      <Grid container spacing={2}>
        <Grid maxWidth={"100%"} width={"100%"} size={{ md: 5.5, sm: 12 }}>
          <Box width={"100%"}>
            {newJobRule.postActions && newJobRule.postActions.length > 0 && (
              <DataTable
                noControls
                selectedRows={selectedPostActionRow !== null ? [selectedPostActionRow] : []}
                onRowClick={(params) => setSelectedPostActionsRow(params?.row?.id as number)}
                rows={newJobRule.postActions.map((x, i) => ({ ...x, id: i })) || []}
                columns={postActionsCols.map((c) => ({ ...c, sortable: false }))}
                rowHeight={32}
                columnHeaderHeight={32}
                disableChildrenSorting
                disableMultipleColumnsSorting
                sx={{ pb: 1.5, height: "auto", width: "100%" }}
              />
            )}
          </Box>
          <Box display={"flex"} flexDirection={"row"}>
            <Select
              IconComponent={ExpandMoreRoundedIcon}
              size="small"
              sx={{ width: "60%", mr: 2 }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              displayEmpty
              onChange={(e) => setSelectedNewPostActionType(e.target.value as PostActionType)}
              value={selectedNewPostActionType}
            >
              <MenuItem value="SendEmail">{t("menu:item.sendEmail")}</MenuItem>
              <MenuItem value="SetMetadata">{t("menu:item.setMetadata")}</MenuItem>
              <MenuItem value="SetStatus">{t("menu:item.setStatus")}</MenuItem>
              <MenuItem value="IngestStart">{t("menu:item.ingestStart")}</MenuItem>
            </Select>
            <Button disabled={!selectedNewPostActionType} onClick={handleAddPostAction}>
              {t("button:addAction")}
            </Button>
          </Box>
        </Grid>
        {selectedPostActionRow !== null &&
          newJobRule?.postActions &&
          newJobRule?.postActions[selectedPostActionRow] && (
            <Grid size={{ md: 6.5, sm: 12 }}>
              <Stack direction={"column"} spacing={1}>
                <FormAutocomplete
                  label={t("field:label.triggerOnJobStatuses")}
                  value={[...(newJobRule.postActions![selectedPostActionRow].jobStatuses || [])]}
                  multiple
                  fullWidth
                  options={[...Object.values(JobStatus)] as JobStatus[]}
                  onChange={(v) =>
                    setNewJobRule(
                      produce((draft) => {
                        draft!.postActions = draft!.postActions!.map((x, i) =>
                          i === selectedPostActionRow ? { ...x, jobStatuses: v } : x
                        );
                      })
                    )
                  }
                  validationProps={{
                    formId: formId,
                    formSectionId: newJobRule.postActions![selectedPostActionRow].uuid!,
                    fieldId: `postActionJobStatusTrigger-${newJobRule.postActions![selectedPostActionRow].uuid}`,
                    required: true,
                    minLength: 1,
                    initialValue: [...(newJobRule.postActions![selectedPostActionRow].jobStatuses || [])],
                  }}
                />
                {newJobRule.postActions![selectedPostActionRow].type === PostActionType.SetStatus && (
                  <FormAutocomplete
                    label={t("field:label.applyObjectStatus")}
                    value={newJobRule?.postActions![selectedPostActionRow].status ?? null}
                    fullWidth
                    onChange={(v) =>
                      setNewJobRule(
                        produce((draft) => {
                          draft!.postActions = draft!.postActions!.map((x, i) =>
                            i === selectedPostActionRow ? { ...x, status: v } : x
                          );
                        })
                      )
                    }
                    options={Object.values(ObjectStatus)}
                    filterOptions={(x, s) => {
                      let match = Object.values(ObjectStatus).filter((x) =>
                        x.toLowerCase().includes(s.inputValue.toLowerCase())
                      );
                      return x.filter((a) => a && match.includes(a));
                    }}
                    validationProps={{
                      formId: formId,
                      formSectionId: newJobRule.postActions![selectedPostActionRow].uuid!,
                      fieldId: `postActionApplyObjectStatus-${newJobRule.postActions![selectedPostActionRow].uuid}`,
                      required: true,
                      initialValue: newJobRule.postActions![selectedPostActionRow].status ?? null,
                    }}
                  />
                )}
                {newJobRule.postActions![selectedPostActionRow].type === PostActionType.SetMetadata && (
                  <FormAutocomplete
                    label={t("field:label.chooseMetadata")}
                    fullWidth
                    value={
                      metas.find((x) => x.id === newJobRule?.postActions![selectedPostActionRow].metadata?.id)?.id ??
                      null
                    }
                    options={metas
                      .filter(
                        (x) => newJobRule.filter?.metadataMatch?.find((m) => m.metadata?.id === x.id) === undefined
                      )
                      .filter(
                        (x) =>
                          !x.baseType!.includes("Localized") &&
                          !x.baseType!.includes("Casts") &&
                          !x.baseType!.includes("Synopnes") &&
                          !x.isUsedOnBundleMaker
                      )
                      .map((x) => x.id)}
                    getOptionLabel={(option) => metas.find((x) => x.id === option)?.tagName ?? ""}
                    onChange={(v) => {
                      const selectedMeta = metas.find((x) => x.id === v);
                      if (!selectedMeta) return;
                      const metaValue = CreateNewDbMetadataValue(
                        selectedMeta.id,
                        selectedMeta.baseType!,
                        selectedMeta.tagName!,
                        null
                      );
                      setNewJobRule(
                        produce((draft) => {
                          draft!.postActions = draft!.postActions!.map((x, i) =>
                            i === selectedPostActionRow
                              ? {
                                  ...x,
                                  metadata: metaValue,
                                }
                              : x
                          );
                        })
                      );
                    }}
                    validationProps={{
                      formId: formId,
                      formSectionId: newJobRule.postActions![selectedPostActionRow].uuid!,
                      fieldId: `postActionMetadataField-${newJobRule.postActions![selectedPostActionRow].uuid}`,
                      required: true,
                      initialState: false,
                      initialValue:
                        metas.find((x) => x.id === newJobRule?.postActions![selectedPostActionRow].metadata?.id)?.id ??
                        null,
                    }}
                  />
                )}
                {newJobRule.postActions![selectedPostActionRow].type === PostActionType.SetMetadata &&
                  newJobRule?.postActions![selectedPostActionRow].metadata?.id && (
                    <MetadataInputIndex
                      data={metas.find((x) => x.id === newJobRule!.postActions![selectedPostActionRow].metadata!.id)!}
                      value={
                        metas.find((x) => x.id === newJobRule!.postActions![selectedPostActionRow].metadata!.id)
                          ?.baseType === BaseMetadataType.DbStringMetadataValueInfos
                          ? newJobRule?.postActions![selectedPostActionRow].metadata?.value
                          : newJobRule?.postActions![selectedPostActionRow].metadata
                      }
                      onChange={(v, fieldId, valueIndex) => {
                        setNewJobRule(
                          produce((draft) => {
                            draft!.postActions = draft!.postActions!.map((x, i) =>
                              i === selectedPostActionRow
                                ? { ...x, metadata: { ...x.metadata!, value: v.value ? v.value : v } }
                                : x
                            );
                          })
                        );
                      }}
                    />
                  )}
              </Stack>
            </Grid>
          )}
      </Grid>
    </FormSection>
  );
};

export default EditPostActions;
